import SecureLS from 'secure-ls';

const ls = new SecureLS({ encodingType: 'aes' });

// API Configuration
const API_CONFIG = {
  // Base URL for the Django backend
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  
  // API endpoints
  ENDPOINTS: {
    THOUGHTS: '/api/thoughts',
    AUTH: '/api/auth',
    USERS: '/api/users',
  },
  
  // Default headers
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
  },
  
  // Timeout for API calls (in milliseconds)
  TIMEOUT: 10000,
};

// Helper function to get auth token
const getAuthToken = () => {
  // Use the same SecureLS storage as the rest of the application
  return ls.get('authToken');
};

// Helper function to set auth token
const setAuthToken = (token) => {
  // Store token using SecureLS (same as login component)
  ls.set('authToken', token);
};

// Helper function to remove auth token
const removeAuthToken = () => {
  // Remove token using SecureLS
  ls.remove('authToken');
};

// Helper function to check if user is logged in
const isLoggedIn = () => {
  const token = ls.get('authToken');
  return !!token;
};

// API call helper function
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  const token = getAuthToken();
  
  // Add X-Family-Id header for multi-family context
  const familyId = localStorage.getItem('familyId');

  const config = {
    headers: {
      ...API_CONFIG.DEFAULT_HEADERS,
      ...(token && { 'Authorization': `Token ${token}` }),
      ...(familyId && { 'X-Family-Id': familyId }),
      ...options.headers,
    },
    ...options,
  };
  
  // Add timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);
  
  try {
    const response = await fetch(url, {
      ...config,
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    
    console.error('API call failed:', error);
    throw error;
  }
};

// Specific API functions for thoughts
const thoughtsAPI = {
  // Get all mood options
  getMoods: () => apiCall(`${API_CONFIG.ENDPOINTS.THOUGHTS}/moods/`),
  
  // Get recent thoughts
  getRecentThoughts: () => apiCall(`${API_CONFIG.ENDPOINTS.THOUGHTS}/thoughts/recent/`),
  
  // Get weekly statistics
  getWeeklyStats: () => apiCall(`${API_CONFIG.ENDPOINTS.THOUGHTS}/thoughts/weekly_stats/`),
  
  // Get random prompt
  getRandomPrompt: () => apiCall(`${API_CONFIG.ENDPOINTS.THOUGHTS}/prompts/random/`),
  
  // Get today's thought
  getTodayThought: async () => {
    const response = await apiCall(`${API_CONFIG.ENDPOINTS.THOUGHTS}/thoughts/today/`);
    
    // Check if the response indicates no thought for today
    if (response.has_thought === false) {
      return null; // Return null to indicate no thought exists
    }
    
    // Return the thought data if it exists
    return response;
  },
  
  // Create a new thought
  createThought: (thoughtData) => apiCall(`${API_CONFIG.ENDPOINTS.THOUGHTS}/thoughts/`, {
    method: 'POST',
    body: JSON.stringify(thoughtData),
  }),
  
  // Add reaction to thought
  addReaction: (thoughtId, reactionType) => apiCall(`${API_CONFIG.ENDPOINTS.THOUGHTS}/thoughts/${thoughtId}/add_reaction/`, {
    method: 'POST',
    body: JSON.stringify({ reaction_type: reactionType }),
  }),
  
  // Add comment to thought
  addComment: (thoughtId, commentData) => apiCall(`${API_CONFIG.ENDPOINTS.THOUGHTS}/thoughts/${thoughtId}/add_comment/`, {
    method: 'POST',
    body: JSON.stringify(commentData),
  }),
  
  // Upload media
  uploadMedia: (mediaData) => {
    const formData = new FormData();
    Object.keys(mediaData).forEach(key => {
      formData.append(key, mediaData[key]);
    });
    
    return apiCall(`${API_CONFIG.ENDPOINTS.THOUGHTS}/media/`, {
      method: 'POST',
      headers: {
        // Don't set Content-Type for FormData - let the browser set it automatically
      },
      body: formData,
    });
  },
};

export {
  API_CONFIG,
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  isLoggedIn,
  apiCall,
  thoughtsAPI,
}; 